import React from "react";
import useScrollReveal from "../../hooks/useScrollReveal";

/**
 * <SplitText>: break children text into word-spans, each revealing with
 * staggered translateY + opacity. Framer-style word-by-word intro.
 *
 * Accepts plain text OR mixed children (strings + <span>/<em> etc). For
 * complex JSX nodes we just render them as-is after the text spans.
 *
 * Props:
 *   - as: tag (default h1)
 *   - stagger: ms between words (default 60)
 *   - baseDelay: ms delay before first word (default 0)
 *   - className: class for outer element
 */
export default function SplitText({
  as: Tag = "h1",
  stagger = 60,
  baseDelay = 0,
  className = "",
  children,
  ...rest
}) {
  const [ref, revealed] = useScrollReveal({ threshold: 0.25 });

  // Split text nodes into word spans. Preserve non-text nodes.
  const nodes = React.Children.toArray(children);
  let wordCounter = 0;
  const rendered = nodes.map((node, nodeIdx) => {
    if (typeof node === "string") {
      const words = node.split(/(\s+)/); // keep whitespace
      return words.map((word, wi) => {
        if (/^\s+$/.test(word)) {
          return (
            <React.Fragment key={`${nodeIdx}-ws-${wi}`}>{word}</React.Fragment>
          );
        }
        const idx = wordCounter++;
        return (
          <span
            key={`${nodeIdx}-w-${wi}`}
            className="split-word"
            style={{ transitionDelay: `${baseDelay + idx * stagger}ms` }}
          >
            {word}
          </span>
        );
      });
    }
    // React element (e.g. <span className="gradient-text">) — wrap as a
    // single "word" so it participates in the stagger too.
    if (React.isValidElement(node)) {
      const idx = wordCounter++;
      return React.cloneElement(
        node,
        {
          key: `${nodeIdx}-el`,
          className: [node.props.className, "split-word"]
            .filter(Boolean)
            .join(" "),
          style: {
            ...(node.props.style || {}),
            transitionDelay: `${baseDelay + idx * stagger}ms`,
          },
        },
        node.props.children
      );
    }
    return node;
  });

  return (
    <Tag
      ref={ref}
      className={`split-text ${className}`}
      data-revealed={revealed ? "true" : "false"}
      {...rest}
    >
      {rendered}
    </Tag>
  );
}
